import { ref } from 'vue'
import env from '@/env'

const POLL_INTERVAL_MS = 5 * 60 * 1000 // every 5 minutes
const INITIAL_DELAY_MS = 30 * 1000 // first check after 30s

export const currentVersion = String(env.version || '')
export const remoteVersion = ref<string | null>(null)

let started = false
let intervalId: ReturnType<typeof setInterval> | null = null

const fetchRemoteVersion = async (): Promise<string | null> => {
	try {
		const resp = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
		if (!resp.ok) return null
		const data = await resp.json()
		return typeof data?.version === 'string' ? data.version : null
	} catch {
		return null
	}
}

const checkVersion = async () => {
	if (remoteVersion.value) return
	const remote = await fetchRemoteVersion()
	if (!remote || remote === currentVersion) return
	remoteVersion.value = remote
	if (intervalId) {
		clearInterval(intervalId)
		intervalId = null
	}
}

// Hard reload: clear CacheStorage entries (used by service worker), unregister
// any service workers, then navigate to a cache-busted URL so HTTP/disk cache
// is bypassed for the entry document.
export const hardReload = async () => {
	try {
		if ('caches' in window) {
			const keys = await caches.keys()
			await Promise.all(keys.map((k) => caches.delete(k)))
		}
		if ('serviceWorker' in navigator) {
			const regs = await navigator.serviceWorker.getRegistrations()
			await Promise.all(regs.map((r) => r.unregister()))
		}
	} catch (e) {
		console.warn('hardReload cleanup failed', e)
	}
	const url = new URL(window.location.href)
	url.searchParams.set('_v', Date.now().toString())
	window.location.replace(url.toString())
}

export const startVersionChecker = () => {
	if (started) return
	started = true

	setTimeout(() => {
		checkVersion()
		intervalId = setInterval(checkVersion, POLL_INTERVAL_MS)
	}, INITIAL_DELAY_MS)

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') checkVersion()
	})
}
